import { h } from 'preact';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import Siema from 'siema';

import { IconArrowLink, IconArrowPlayerBoldDx, IconArrowPlayerBoldSx } from '../../1-foundation/icons/icons';
import { useIsDragging } from '../../../hooks/use-is-dragging';
import { useChangeOrientation } from '../../../hooks/use-change-orientation';
import { useForceRemount } from '../../../hooks/use-force-remount';
import { handleCarouselKeyboardNavigation } from '../../../../utils/carousel-utils';
import { getClassNames } from '../../../../utils/classnames-utils';
import { isMobile } from '../../../../utils/device-utils';
import { ASPECT_RATIO_16_10, ASPECT_RATIO_16_9 } from '../../3-molecules/card/card';

import { modifierList } from './props-list';
import { getCardRenderers } from './renderers';
import {
  getHandleCardClick,
  getHandleNextClick,
  getHandleNextKeydown,
  getHandlePrevClick,
  getHandlePrevKeydown,
} from './handlers';
import { C_VIDEO_CARD_INLINE_CLASSNAME } from './costants';

const EDITORIAL_PREMIUM_CLASS = 'editorial-premium';
const NARROW_ARTICLE_CLASS = 'narrow-article';
const MOBILE_DEVICE = 'mobile';
const DESKTOP_DEVICE = 'desktop';

const PlaylistCarouselInline = ({
  title,
  readMore,
  playlistVideos,
  cardsToSlide,
  cardTag,
  cardWidth,
  onCardClick,
  cardList,
  modifiers = [],
  cardsStyles = {},
}) => {
  const deviceType = isMobile() ? MOBILE_DEVICE : DESKTOP_DEVICE;
  const isNarrowArticle = modifiers[deviceType]?.includes(NARROW_ARTICLE_CLASS);
  const arrowColorModifier = modifiers[deviceType]?.includes(EDITORIAL_PREMIUM_CLASS) ? 'c-dark' : 'c-light';
  const additionalClasses = isNarrowArticle ? 'l-pull-narrow-article--right-d l-pull--right-m' : 'l-pull';
  const classNames = `${getClassNames('c-playlist-carousel-inline', modifiers[deviceType])} ${additionalClasses}`;
  const cards = playlistVideos || cardList || [];

  // HOOKS
  const prevButtonRef = useRef();
  const nextButtonRef = useRef();
  const sliderInstance = useRef();
  const [isFirstCard, setIsFirstCard] = useState(true);
  const [isLastCard, setIsLastCard] = useState(false);
  const [key, forceRemount] = useForceRemount();
  const [cardsWrapperRef, isDragging] = useIsDragging();
  const cardInstance = useRef();
  useChangeOrientation(forceRemount);

  // HANDLERS
  const handleCardClick = getHandleCardClick(isDragging, onCardClick);
  const handlePrevClick = useCallback(getHandlePrevClick(sliderInstance, cardsToSlide), [sliderInstance]);
  const handleNextClick = useCallback(getHandleNextClick(sliderInstance, cardsToSlide), [sliderInstance]);
  const handlePrevKeydown = useCallback(getHandlePrevKeydown(handlePrevClick), [handlePrevClick]);
  const handleNextKeydown = useCallback(getHandleNextKeydown(handleNextClick), [handleNextClick]);

  // CARDS RENDERERS
  const cardsClassname = cardsStyles[deviceType]?.className || C_VIDEO_CARD_INLINE_CLASSNAME;
  const genericCardRenderer = getCardRenderers({ cardsStyles, cardTag, deviceType, cardInstance, handleCardClick })[
    cardsClassname
  ];

  useEffect(() => {
    if (cards.length) {
      const slidesToShow = cardsWrapperRef.current.offsetWidth / cardWidth[cardsClassname][deviceType];
      sliderInstance.current = new Siema({
        selector: cardsWrapperRef.current,
        perPage: slidesToShow,
        onChange: () => {
          sliderInstance.current.drag.preventClick = true; // Prevent click on the card when dragging

          // Prev button management
          if (sliderInstance.current.currentSlide === 0) {
            setIsFirstCard(true);
          } else {
            setIsFirstCard(false);
          }

          // Next button management
          const checkIsLastCard = () =>
            Math.round((cards.length - sliderInstance.current.currentSlide - slidesToShow) * 10) / 10 === 0;
          if (checkIsLastCard()) {
            setIsLastCard(true);
          } else {
            setIsLastCard(false);
          }
        },
      });

      const unbindKeyboardNavigation = handleCarouselKeyboardNavigation({
        carousel: sliderInstance.current,
        slideSelector: cardsClassname,
      });

      return () => {
        sliderInstance.current.destroy();
        unbindKeyboardNavigation();
      };
    }
  }, [cards.length, key]);

  return (
    <aside key={key} className={classNames}>
      <div className="c-playlist-carousel-inline__header">
        <h2 className="c-playlist-carousel-inline__section-title">{title}</h2>
        <div class="c-playlist-carousel-inline__arrows">
          <IconArrowPlayerBoldSx
            ref={prevButtonRef}
            classNames={isFirstCard ? 'is-disabled' : ''}
            modifiers={['arrow', 'arrow-player-bold-sx', 'small', arrowColorModifier]}
            onClick={handlePrevClick}
            onKeyDown={handlePrevKeydown}
            tabindex="0"
            role="button"
            aria-label="Scorri i contenuti precedenti"
          />
          <IconArrowPlayerBoldDx
            ref={nextButtonRef}
            classNames={isLastCard ? 'is-disabled' : ''}
            modifiers={['arrow', 'arrow-player-bold-dx', 'small', arrowColorModifier]}
            onClick={handleNextClick}
            onKeyDown={handleNextKeydown}
            tabindex="0"
            role="button"
            aria-label="Scorri i contenuti successivi"
          />
        </div>
      </div>
      <div
        ref={cardsWrapperRef}
        className="c-playlist-carousel-inline__cards-wrapper"
        data-state={isLastCard ? 'is-last-card' : ''}
      >
        {cards && cards.map(genericCardRenderer)}
      </div>
      {readMore && (
        <a href={readMore.url || '#'} className="c-playlist-carousel-inline__read-more" data-intcmp={readMore.intcmp}>
          <span>{readMore.text}</span>
          <IconArrowLink modifiers={['arrow-navigation', 'arrow-link', 'xsmall', 'c-dark']} />
        </a>
      )}
    </aside>
  );
};

PlaylistCarouselInline.propTypes = {
  title: PropTypes.string,
  readMore: PropTypes.shape({
    text: PropTypes.string,
    url: PropTypes.string,
    intcmp: PropTypes.string,
  }),
  cardsToSlide: PropTypes.number.isRequired,
  cardTag: PropTypes.string,
  cardWidth: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      mobile: PropTypes.number,
      desktop: PropTypes.number,
    }),
  }).isRequired,
  onCardClick: PropTypes.func.isRequired,
  modifiers: PropTypes.shape({
    mobile: PropTypes.arrayOf(PropTypes.oneOf(modifierList)),
    desktop: PropTypes.arrayOf(PropTypes.oneOf(modifierList)),
  }),
  cardsStyles: PropTypes.shape({
    mobile: PropTypes.shape({
      className: PropTypes.string,
      modifiers: PropTypes.arrayOf(PropTypes.string),
    }),
    desktop: PropTypes.shape({
      className: PropTypes.string,
      modifiers: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
  playlistVideos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      imageSrc: PropTypes.string.isRequired,
      duration: PropTypes.string.isRequired,
      publishedTime: PropTypes.string.isRequired,
      isPlaying: PropTypes.bool,
    })
  ),
  cardList: PropTypes.arrayOf(
    PropTypes.shape({
      section: PropTypes.string,
      title: PropTypes.string.isRequired,
      url: PropTypes.string,
      urlImage: PropTypes.shape({
        mobile: PropTypes.string,
        desktop: PropTypes.string,
      }),
      intcmp: PropTypes.string,
      target: PropTypes.string,
      time: PropTypes.string,
      rel: PropTypes.string,
      imageAspectRatio: PropTypes.shape({
        mobile: PropTypes.oneOf([ASPECT_RATIO_16_9, ASPECT_RATIO_16_10, '']),
        desktop: PropTypes.oneOf([ASPECT_RATIO_16_9, ASPECT_RATIO_16_10, '']),
      }),
      labelModifiers: PropTypes.array,
    })
  ),
};

export default PlaylistCarouselInline;
